import { create } from "zustand";

/**
 * Open state of the chat and accessibility panels.
 *
 * On phones neither has a floating button any more: a button fixed over a
 * two-column page always ends up on something (it sat on the product price and
 * on Quick Add buttons). Chat opens from the bottom nav and the accessibility
 * options from the menu, so the launcher and the panel are different
 * components. `a11yReady` lets the menu offer its entry only while the panel
 * exists (the widget is not mounted behind the cookie banner). The homepage
 * has neither a bottom nav nor floating buttons: its menu opens both panels.
 */
interface FloatingPanelsStore {
  chatOpen: boolean;
  /** The chat has been opened on this visit: keep it mounted, so closing it keeps the conversation. */
  chatUsed: boolean;
  /** Where keyboard focus returns when the chat closes. */
  chatOpener: HTMLElement | null;
  a11yReady: boolean;
  a11yOpen: boolean;
  /** Where keyboard focus returns when the accessibility panel closes. */
  a11yOpener: HTMLElement | null;
  setChatOpen: (open: boolean, opener?: HTMLElement | null) => void;
  setA11yReady: (ready: boolean) => void;
  /**
   * `opener` is the control focus goes back to. The menu entry passes the
   * hamburger button, not itself: the entry unmounts with the menu.
   */
  setA11yOpen: (open: boolean, opener?: HTMLElement | null) => void;
}

export const useFloatingPanels = create<FloatingPanelsStore>()((set) => ({
  chatOpen: false,
  chatUsed: false,
  chatOpener: null,
  a11yReady: false,
  a11yOpen: false,
  a11yOpener: null,
  setChatOpen: (open, opener) => set(open ? { chatOpen: true, chatUsed: true, chatOpener: opener ?? null } : { chatOpen: false }),
  // A widget that unmounts takes its panel with it.
  setA11yReady: (ready) => set(ready ? { a11yReady: true } : { a11yReady: false, a11yOpen: false, a11yOpener: null }),
  // The opener outlives the close: the panel reads it to hand focus back.
  setA11yOpen: (open, opener) => set(open ? { a11yOpen: true, a11yOpener: opener ?? null } : { a11yOpen: false }),
}));
